import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAuth } from '../features/auth/hooks/useAuth'
import type { RootState } from '../shared/store'
import { Button } from '../shared/components/ui/button'
import { Badge } from '../shared/components/ui/badge'
import { Avatar, AvatarFallback } from '../shared/components/ui/avatar'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../shared/components/ui/sheet'
import { userMenuItems, visibleNavItems } from './navigation'
import { LogOutIcon } from 'lucide-react'

interface UserDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const drawerLinkClassName =
  'inline-flex items-center justify-start whitespace-nowrap rounded-full text-sm font-medium transition-colors border border-border h-13 px-5 w-full hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]'

/**
 * UserDrawer component
 * - Side drawer for user menu on mobile devices (right side)
 * - Shows user info and account actions
 */
export const UserDrawer = ({ isOpen, onClose }: UserDrawerProps) => {
  const { user, logout, role } = useAuth()
  const navigate = useNavigate()
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount)

  const handleLogout = async () => {
    await logout()
    onClose()
    void navigate('/login')
  }

  const getInitials = (name: string | undefined) => {
    if (!name) return '?'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        hideClose
        className="flex w-[312px] flex-col rounded-l-3xl bg-sidebar p-0 text-sidebar-foreground"
      >
        {/* Header - Fixed */}
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="text-left">Account</SheetTitle>
          <SheetDescription className="sr-only">
            User account information and settings
          </SheetDescription>
        </SheetHeader>

        {/* Hidden focusable element to prevent auto-focus on Logout button */}
        <div tabIndex={0} className="sr-only" aria-hidden="true" />

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* User Info */}
          <div className="px-6 py-6">
            <div className="flex items-center space-x-4 mb-6">
              <Avatar className="h-12 w-12">
                <AvatarFallback>
                  {getInitials(user?.user_metadata?.full_name as string | undefined)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{user?.user_metadata?.full_name || 'User'}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {role && <p className="text-xs text-muted-foreground capitalize">Role: {role}</p>}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {visibleNavItems(userMenuItems, role).map((item) => (
                <Link key={item.to} to={item.to} onClick={onClose} className={drawerLinkClassName}>
                  {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                  {item.label}
                  {item.showsUnreadBadge && unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-auto">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Link>
              ))}
              <Button
                variant="destructive"
                className="w-full justify-start"
                onClick={() => void handleLogout()}
              >
                <LogOutIcon className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
